import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import Modal from '../Modal/Modal';

import ChevronIcon from '../../assets/svgs/ChevronIcon';
import DeleteReportIcon from '../../assets/svgs/DeleteReportIcon';
import LoadingImage from '../../assets/svgs/LoadingImage';
import ZoomIcon from '../../assets/svgs/ZoomIcon';

import formatDate from '../../utils/formatDate';
import toTitleCase from '../../utils/toTitleCase';
import translate from '../../libs/i18n';
import { examSerieDefaultProps } from '../../utils/defaultProps';
import { examSerieShape } from '../../utils/propTypesShapes';

import { AddExamSerieStyle } from './AddExamSerieStyle';

const AddExamSerie = ({ serie, handleDeleteSerie }) => {
  const language = useSelector((state) => state.settings.language);

  const [modalPage, setModalPage] = useState(0);
  const [showModal, setShowModal] = useState(false);

const PREVIEW_COUNT = 5;

const firstItemImageFile = useMemo(() => {
  if (!serie || !serie.dicomImages || !serie.dicomImages.length) return '';

  for (let i = 0; i < serie.dicomImages.length; i += 1) {
    const images = serie.dicomImages[i];
    const jpeg = images?.jpegs?.[0]?.jpeg || '';
    if (jpeg && jpeg.length) return jpeg;
  }

  return '';
}, [serie]);

const previewThumbnails = useMemo(() => {
  if (!serie || !serie.dicomImages || !serie.dicomImages.length) return [];

  return serie.dicomImages
    .map((images, index) => {
      const jpeg = images?.jpegs?.[0]?.jpeg || '';
      if (!jpeg || !jpeg.length) return null;
      return { id: images.id || `${index}`, jpeg, index };
    })
    .filter((x) => x);
}, [serie]);

  const handleChangePage = (value) => {
    let currentModalPage = modalPage + value;

    if (currentModalPage <= 0) {
      currentModalPage = 0;
    }

    if (
      serie &&
      serie.dicomImages &&
      serie.dicomImages.length &&
      currentModalPage >= serie.dicomImages.length - 1
    ) {
      currentModalPage = serie.dicomImages.length - 1;
    }

    setModalPage(currentModalPage);
  };

  return (
    <AddExamSerieStyle>
      <div className="exam-serie-container">
        {serie.uploaded && firstItemImageFile.length ? (
          <button
            className="show-serie-image-modal"
            type="button"
            onClick={() => setShowModal(true)}
          >
            <img
              className="serie-first-image"
              src={firstItemImageFile}
              alt={serie.seriesDescription}
            />
            <span className="icon">
              <ZoomIcon />
            </span>
          </button>
        ) : (
          <div className="serie-image-loading">
            <span className="loading-wrapper">
              <LoadingImage />
            </span>
          </div>
        )}
      </div>
      <div className="serie-informations">
        {serie.seriesDescription && serie.seriesDescription.length ? (
          <div className="serie-description">
            {toTitleCase(serie.seriesDescription)}
          </div>
        ) : null}
        {serie.seriesDate && serie.seriesDate.length ? (
          <div className="serie-date">
            {formatDate(
              serie.seriesDate,
              language === 'pt-br' ? 'dd/mm/yyyy' : 'mm/dd/yyyy'
            )}
          </div>
        ) : null}
        <div className="serie-images-count">
          {`${serie.imagesCount} ${translate(
            `${serie.imagesCount === 1 ? 'image' : 'images'}`,
            language
          )}`}
        </div>
        <div
          className={`serie-images-status${
            serie.uploaded ? ' uploaded' : ' uploading'
          }`}
        >
          {translate(`${serie.uploaded ? 'uploaded' : 'uploading'}`, language)}
        </div>
      </div>
      <button
        className="delete-exam-serie-button"
        type="button"
        disabled={!serie.uploaded}
        onClick={handleDeleteSerie}
      >
        <DeleteReportIcon />
      </button>
      {showModal ? (
        <Modal theme="light" handleClose={() => setShowModal(false)}>
          <>
            <div className="serie-image-modal-container">
              <div className="image-modal-navigation">
                {previewThumbnails && previewThumbnails.length
                  ? previewThumbnails.map((item) => {
                      const { jpeg, id, index } = item;

                      return (
                        <div
                          key={id}
                          className={`modal-navigation-item${
                            index === modalPage ? ' active' : ''
                          }`}
                          style={{
                            transform: `translateY(calc(${-100 * modalPage}% + ${modalPage === 0 ? 0 : 40}px))`,
                          }}
                        >
                          {serie &&
                          ((serie.seriesDescription && serie.seriesDescription.length) ||
                            (serie.seriesDate && serie.seriesDate.length)) ? (
                            <div className="navigation-item-text">
                              {`${
                                serie.seriesDescription && serie.seriesDescription.length
                                  ? toTitleCase(serie.seriesDescription)
                                  : ''
                              }${
                                serie.seriesDescription && serie.seriesDescription.length &&
                                serie.seriesDate && serie.seriesDate.length
                                  ? ', '
                                  : ''
                              }${
                                serie.seriesDate && serie.seriesDate.length
                                  ? formatDate(
                                      serie.seriesDate,
                                      language === 'pt-br' ? 'dd/mm/yyyy' : 'mm/dd/yyyy'
                                    )
                                  : ''
                              }`}
                            </div>
                          ) : null}
                          <button
                            className="navigation-item-button"
                            type="button"
                            onClick={() => setModalPage(index)}
                          >
                            <img
                              className="item-button-image"
                              src={jpeg}
                              alt={`Serie ${index}`}
                            />
                          </button>
                        </div>
                      );
                    })
                  : null}
              </div>
              <div className="image-modal-carousel">
                <button
                  className="modal-carousel-navigation-step last-image"
                  type="button"
                  disabled={modalPage === 0}
                  onClick={() => handleChangePage(-1)}
                >
                  <span className="icon">
                    <ChevronIcon />
                  </span>
                </button>
                <button
                  className="modal-carousel-navigation-step next-image"
                  type="button"
                  disabled={modalPage === serie.dicomImages.length - 1}
                  onClick={() => handleChangePage(1)}
                >
                  <span className="icon">
                    <ChevronIcon />
                  </span>
                </button>
                <div className="modal-carousel-content">
                  {serie && serie.dicomImages && serie.dicomImages.length
                    ? serie.dicomImages.map((images, index) => {
                        let jpeg = '';
                        if (
                          images &&
                          images.id &&
                          images.jpegs &&
                          images.jpegs &&
                          images.jpegs[0] &&
                          images.jpegs[0].jpeg &&
                          images.jpegs[0].jpeg.length
                        ) {
                          jpeg = images.jpegs[0].jpeg;
                        }

                        if (jpeg.length) {
                          return (
                            <div
                              key={images.id}
                              className="carousel-content-item"
                              style={{
                                transform: `translateX(${-100 * modalPage}%)`,
                              }}
                            >
                              <img
                                className="content-item-image"
                                src={jpeg}
                                alt={`Serie ${index}`}
                              />
                            </div>
                          );
                        }
                        return null;
                      })
                    : null}
                </div>
              </div>
            </div>
          </>
        </Modal>
      ) : null}
    </AddExamSerieStyle>
  );
};

AddExamSerie.propTypes = {
  handleDeleteSerie: PropTypes.func.isRequired,
  serie: examSerieShape,
};

AddExamSerie.defaultProps = {
  serie: examSerieDefaultProps,
};

export default AddExamSerie;
